import { useReducer, useCallback, useRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Mic } from 'lucide-react';
import { toast } from 'react-hot-toast';
import type { PerioMeasurement, PerioSite } from '@/utils/perio';
import { PERIO_SITES, isMultiRooted } from '@/utils/perio';
import { useRecordPerioExam } from '@/hooks/usePerioExams';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface PerioGridState {
  /** exam_id being edited (null = new exam, will be assigned on first save) */
  examId: string | null;
  /** measurements keyed by "fdi:site" */
  cells: Record<string, CellValue>;
  /** teeth marked as missing */
  missingTeeth: Set<string>;
  isDirty: boolean;
}

export interface CellValue {
  pocket_depth_mm: number | null;
  recession_mm: number | null;
  bleeding_on_probing: boolean;
  suppuration: boolean;
  mobility: number | null;   // per tooth, stored on each site
  furcation: number | null;  // per tooth
}

type GridAction =
  | { type: 'SET_CELL'; fdi: string; site: PerioSite; field: keyof CellValue; value: number | boolean | null }
  | { type: 'TOGGLE_MISSING'; fdi: string }
  | { type: 'LOAD_EXAM'; examId: string; measurements: PerioMeasurement[] }
  | { type: 'NEW_EXAM' }
  | { type: 'SAVED' };

function defaultCell(): CellValue {
  return {
    pocket_depth_mm: null,
    recession_mm: null,
    bleeding_on_probing: false,
    suppuration: false,
    mobility: null,
    furcation: null,
  };
}

function gridReducer(state: PerioGridState, action: GridAction): PerioGridState {
  switch (action.type) {
    case 'SET_CELL': {
      const key = `${action.fdi}:${action.site}`;
      const existing = state.cells[key] ?? defaultCell();
      return {
        ...state,
        isDirty: true,
        cells: {
          ...state.cells,
          [key]: { ...existing, [action.field]: action.value },
        },
      };
    }
    case 'TOGGLE_MISSING': {
      const next = new Set(state.missingTeeth);
      if (next.has(action.fdi)) next.delete(action.fdi);
      else next.add(action.fdi);
      return { ...state, isDirty: true, missingTeeth: next };
    }
    case 'LOAD_EXAM': {
      const cells: Record<string, CellValue> = {};
      for (const m of action.measurements) {
        cells[`${m.tooth_fdi}:${m.site}`] = {
          pocket_depth_mm: m.pocket_depth_mm,
          recession_mm: m.recession_mm,
          bleeding_on_probing: m.bleeding_on_probing,
          suppuration: m.suppuration,
          mobility: m.mobility,
          furcation: m.furcation,
        };
      }
      return { examId: action.examId, cells, missingTeeth: new Set(), isDirty: false };
    }
    case 'NEW_EXAM':
      return { examId: null, cells: {}, missingTeeth: new Set(), isDirty: false };
    case 'SAVED':
      return { ...state, isDirty: false };
    default:
      return state;
  }
}

// ── PD color coding ───────────────────────────────────────────────────────────

function pdInputClass(pd: number | null): string {
  if (pd === null) return 'bg-white dark:bg-gray-800';
  if (pd >= 6) return 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300';
  if (pd >= 4) return 'bg-yellow-50 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300';
  return 'bg-green-50 text-green-700 dark:bg-green-900/40 dark:text-green-300';
}

function calBgClass(cal: number): string {
  if (cal >= 7) return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300';
  if (cal >= 5) return 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300';
  if (cal >= 3) return 'bg-yellow-50 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300';
  return 'bg-gray-50 text-gray-600 dark:bg-gray-800 dark:text-gray-400';
}

// ── Quadrant definitions ──────────────────────────────────────────────────────

const Q1_TEETH = ['18', '17', '16', '15', '14', '13', '12', '11'];
const Q2_TEETH = ['21', '22', '23', '24', '25', '26', '27', '28'];
const Q3_TEETH = ['31', '32', '33', '34', '35', '36', '37', '38'];
const Q4_TEETH = ['41', '42', '43', '44', '45', '46', '47', '48'];

// ── Voice input stub ──────────────────────────────────────────────────────────

function useVoiceInput() {
  return {
    isListening: false,
    start: () => { /* stub */ },
    stop: () => { /* stub */ },
    lastResult: null as string | null,
  };
}

// ── ToothColumn component ─────────────────────────────────────────────────────

interface ToothColumnProps {
  fdi: string;
  cells: Record<string, CellValue>;
  isMissing: boolean;
  isReadOnly?: boolean;
  onDispatch: (action: GridAction) => void;
  /** Returns a ref ID for the first PD input of this tooth (for Tab navigation) */
  getInputRef: (fdi: string, site: PerioSite, field: 'pd' | 'rec') => string;
  highlightedSextantTeeth?: Set<string>;
}

function ToothColumn({
  fdi,
  cells,
  isMissing,
  isReadOnly,
  onDispatch,
  highlightedSextantTeeth,
}: ToothColumnProps) {
  const { t } = useTranslation();
  const isHighlighted = highlightedSextantTeeth?.has(fdi);

  function getCell(site: PerioSite): CellValue {
    return cells[`${fdi}:${site}`] ?? defaultCell();
  }

  const firstCell = getCell('MB');
  const mobility = firstCell.mobility;
  const furcation = firstCell.furcation;

  function handleNumberInput(
    site: PerioSite,
    field: 'pocket_depth_mm' | 'recession_mm',
    raw: string,
  ) {
    const v = raw === '' ? null : Math.min(15, Math.max(0, parseInt(raw, 10) || 0));
    onDispatch({ type: 'SET_CELL', fdi, site, field, value: v });
  }

  return (
    <div
      className={`min-w-[120px] flex-shrink-0 border-r border-gray-200 dark:border-gray-700 ${isHighlighted ? 'bg-indigo-50/50 dark:bg-indigo-900/10' : ''} ${isMissing ? 'opacity-40' : ''}`}
    >
      {/* Tooth header */}
      <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-2 py-1 dark:border-gray-700 dark:bg-gray-800">
        <span className="text-xs font-bold text-gray-700 dark:text-gray-300">{fdi}</span>
        {!isReadOnly && (
          <button
            type="button"
            onClick={() => onDispatch({ type: 'TOGGLE_MISSING', fdi })}
            title={t('dental.perio.missing')}
            className={`rounded px-1 py-0.5 text-xs font-medium transition-colors ${isMissing ? 'bg-gray-400 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-400'}`}
          >
            {t('dental.perio.missing')}
          </button>
        )}
      </div>

      {/* Site rows */}
      {PERIO_SITES.map((site) => {
        const cell = getCell(site);
        const pd = cell.pocket_depth_mm;
        const rec = cell.recession_mm;
        const cal = (pd ?? 0) + (rec ?? 0);

        return (
          <div
            key={site}
            className="flex items-center gap-0.5 border-b border-gray-100 px-1 py-0.5 dark:border-gray-800"
          >
            {/* Site label */}
            <span className="w-6 shrink-0 text-center text-xs font-semibold text-gray-400 dark:text-gray-500">
              {site}
            </span>

            {/* PD */}
            <input
              type="number"
              min={0}
              max={15}
              disabled={isMissing || isReadOnly}
              value={pd ?? ''}
              onChange={e => handleNumberInput(site, 'pocket_depth_mm', e.target.value)}
              data-perio-input={`${fdi}:${site}:pd`}
              aria-label={`PD tand ${fdi} site ${site}`}
              data-field="pocket_depth_mm"
              className={`w-8 rounded border border-gray-200 px-0.5 py-0.5 text-center text-xs font-bold focus:border-indigo-400 focus:outline-none focus:ring-1 focus:ring-indigo-300 dark:border-gray-600 ${pdInputClass(pd)}`}
            />

            {/* Recession */}
            <input
              type="number"
              min={0}
              max={15}
              disabled={isMissing || isReadOnly}
              value={rec ?? ''}
              onChange={e => handleNumberInput(site, 'recession_mm', e.target.value)}
              data-perio-input={`${fdi}:${site}:rec`}
              aria-label={`Rec tand ${fdi} site ${site}`}
              data-field="recession_mm"
              className="w-8 rounded border border-gray-200 px-0.5 py-0.5 text-center text-xs focus:border-indigo-400 focus:outline-none focus:ring-1 focus:ring-indigo-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300"
            />

            {/* CAL (computed, readonly) */}
            <span
              className={`w-6 shrink-0 rounded px-0.5 py-0.5 text-center text-xs font-semibold ${calBgClass(cal)}`}
              aria-label={`CAL tand ${fdi} site ${site}`}
              data-testid="cal-value"
            >
              {pd !== null || rec !== null ? cal : '–'}
            </span>

            {/* BoP */}
            <input
              type="checkbox"
              disabled={isMissing || isReadOnly}
              checked={cell.bleeding_on_probing}
              onChange={e =>
                onDispatch({ type: 'SET_CELL', fdi, site, field: 'bleeding_on_probing', value: e.target.checked })
              }
              title={t('dental.perio.fields.bop')}
              aria-label={`BoP tand ${fdi} site ${site}`}
              className="h-3 w-3 rounded border-gray-300 text-red-500 focus:ring-red-400"
            />

            {/* Suppuration */}
            <input
              type="checkbox"
              disabled={isMissing || isReadOnly}
              checked={cell.suppuration}
              onChange={e =>
                onDispatch({ type: 'SET_CELL', fdi, site, field: 'suppuration', value: e.target.checked })
              }
              title={t('dental.perio.fields.suppuration')}
              aria-label={`Suppuration tand ${fdi} site ${site}`}
              className="h-3 w-3 rounded border-gray-300 text-amber-500 focus:ring-amber-400"
            />
          </div>
        );
      })}

      {/* Per-tooth: Mobility + Furcation */}
      <div className="flex gap-1 border-t border-gray-200 bg-gray-50 px-1 py-1 dark:border-gray-700 dark:bg-gray-800">
        <div className="flex flex-col items-center gap-0.5">
          <span className="text-xs text-gray-400">{t('dental.perio.fields.mobility')}</span>
          <select
            disabled={isMissing || isReadOnly}
            value={mobility ?? ''}
            onChange={e => {
              const v = e.target.value === '' ? null : parseInt(e.target.value, 10);
              PERIO_SITES.forEach(site =>
                onDispatch({ type: 'SET_CELL', fdi, site, field: 'mobility', value: v })
              );
            }}
            className="w-12 rounded border border-gray-200 px-0.5 py-0.5 text-xs dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300"
          >
            <option value="">–</option>
            <option value="0">0</option>
            <option value="1">1</option>
            <option value="2">2</option>
            <option value="3">3</option>
          </select>
        </div>

        {isMultiRooted(fdi) && (
          <div className="flex flex-col items-center gap-0.5">
            <span className="text-xs text-gray-400">{t('dental.perio.fields.furcation')}</span>
            <select
              disabled={isMissing || isReadOnly}
              value={furcation ?? ''}
              onChange={e => {
                const v = e.target.value === '' ? null : parseInt(e.target.value, 10);
                PERIO_SITES.forEach(site =>
                  onDispatch({ type: 'SET_CELL', fdi, site, field: 'furcation', value: v })
                );
              }}
              className="w-12 rounded border border-gray-200 px-0.5 py-0.5 text-xs dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300"
            >
              <option value="">–</option>
              <option value="0">0</option>
              <option value="1">1</option>
              <option value="2">2</option>
              <option value="3">3</option>
            </select>
          </div>
        )}
      </div>
    </div>
  );
}

// ── QuadrantRow component ─────────────────────────────────────────────────────

function QuadrantRow({
  label,
  teeth,
  cells,
  missingTeeth,
  isReadOnly,
  dispatch,
  highlightedSextantTeeth,
}: {
  label: string;
  teeth: string[];
  cells: Record<string, CellValue>;
  missingTeeth: Set<string>;
  isReadOnly?: boolean;
  dispatch: (a: GridAction) => void;
  highlightedSextantTeeth?: Set<string>;
}) {
  return (
    <div className="mb-4">
      <p className="mb-1 px-1 text-xs font-semibold text-gray-500 dark:text-gray-400">{label}</p>
      <div className="flex overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
        {teeth.map(fdi => (
          <ToothColumn
            key={fdi}
            fdi={fdi}
            cells={cells}
            isMissing={missingTeeth.has(fdi)}
            isReadOnly={isReadOnly}
            onDispatch={dispatch}
            getInputRef={() => `${fdi}:MB:pd`}
            highlightedSextantTeeth={highlightedSextantTeeth}
          />
        ))}
      </div>
    </div>
  );
}

// ── PerioGrid component ───────────────────────────────────────────────────────

interface PerioGridProps {
  patientId: string;
  examId: string | null;
  initialMeasurements?: PerioMeasurement[];
  isReadOnly?: boolean;
  highlightedSextantTeeth?: Set<string>;
  onExamIdChange?: (examId: string) => void;
  onDirtyChange?: (isDirty: boolean) => void;
  onMeasurementsChange?: (cells: Record<string, CellValue>) => void;
}

function cellsToMeasurements(
  cells: Record<string, CellValue>,
  missingTeeth: Set<string>,
): Omit<PerioMeasurement, 'cal_mm'>[] {
  const result: Omit<PerioMeasurement, 'cal_mm'>[] = [];
  for (const [key, cell] of Object.entries(cells)) {
    const [fdi, site] = key.split(':') as [string, PerioSite];
    if (missingTeeth.has(fdi)) continue;
    result.push({
      tooth_fdi: fdi,
      site,
      pocket_depth_mm: cell.pocket_depth_mm,
      recession_mm: cell.recession_mm,
      bleeding_on_probing: cell.bleeding_on_probing,
      suppuration: cell.suppuration,
      mobility: cell.mobility,
      furcation: cell.furcation,
    });
  }
  return result;
}

export function PerioGrid({
  patientId,
  examId,
  initialMeasurements = [],
  isReadOnly = false,
  highlightedSextantTeeth,
  onExamIdChange,
  onDirtyChange,
  onMeasurementsChange,
}: PerioGridProps) {
  const { t } = useTranslation();
  const voice = useVoiceInput();
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const recordExam = useRecordPerioExam();

  const [state, dispatch] = useReducer(gridReducer, {
    examId: examId ?? null,
    cells: {},
    missingTeeth: new Set<string>(),
    isDirty: false,
  } satisfies PerioGridState);

  // Load initial measurements when examId changes
  useEffect(() => {
    if (examId && initialMeasurements.length > 0) {
      dispatch({ type: 'LOAD_EXAM', examId, measurements: initialMeasurements });
    } else if (!examId) {
      dispatch({ type: 'NEW_EXAM' });
    }
  }, [examId, initialMeasurements]);

  // Notify parent of dirty state
  useEffect(() => {
    onDirtyChange?.(state.isDirty);
  }, [state.isDirty, onDirtyChange]);

  // Notify parent of cell changes (for PSR recompute)
  useEffect(() => {
    onMeasurementsChange?.(state.cells);
  }, [state.cells, onMeasurementsChange]);

  // Debounced save (600 ms after last change)
  const scheduleSave = useCallback(() => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      const measurements = cellsToMeasurements(state.cells, state.missingTeeth);
      if (measurements.length === 0) return;
      try {
        const returnedId = await recordExam.mutateAsync({
          patientId,
          examId: state.examId,
          measurements,
        });
        dispatch({ type: 'SAVED' });
        if (!state.examId) {
          onExamIdChange?.(returnedId);
        }
      } catch {
        // Silent fail — user can retry on blur
      }
    }, 600);
  }, [state.cells, state.missingTeeth, state.examId, patientId, recordExam, onExamIdChange]);

  // Trigger save on every state change when dirty
  useEffect(() => {
    if (state.isDirty && !isReadOnly) {
      scheduleSave();
    }
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, [state.isDirty, state.cells, state.missingTeeth, isReadOnly, scheduleSave]);

  // ── Voice input ──────────────────────────────────────────────────────────────
  const [, setVoiceListening] = useState(false);

  function handleVoiceClick() {
    setVoiceListening(v => !v);
    toast(t('dental.perio.voiceInputPlaceholder'));
  }

  return (
    <div className="relative">
      {/* Toolbar */}
      {!isReadOnly && (
        <div className="mb-3 flex items-center gap-2">
          {state.isDirty && (
            <span className="text-xs text-amber-600 dark:text-amber-400">Gemmer...</span>
          )}
          <button
            type="button"
            onClick={handleVoiceClick}
            title={t('dental.perio.voiceInputPlaceholder')}
            className={`ml-auto flex items-center gap-1 rounded-lg border px-2 py-1 text-xs transition-colors ${voice.isListening ? 'border-red-400 bg-red-50 text-red-600' : 'border-gray-200 bg-gray-50 text-gray-500 hover:border-indigo-300 hover:text-indigo-600 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-400'}`}
          >
            <Mic className="h-3.5 w-3.5" />
            {t('dental.perio.fields.pd', 'Stemme')}
          </button>
        </div>
      )}

      {/* Column header row */}
      <div className="mb-1 flex items-center gap-1 px-2 text-xs font-semibold text-gray-400 dark:text-gray-500">
        <span className="w-6 text-center">–</span>
        <span className="w-8 text-center">{t('dental.perio.fields.pd')}</span>
        <span className="w-8 text-center">{t('dental.perio.fields.recession')}</span>
        <span className="w-6 text-center">{t('dental.perio.fields.cal')}</span>
        <span className="w-3 text-center">{t('dental.perio.fields.bop')}</span>
        <span className="w-3 text-center">{t('dental.perio.fields.suppuration')}</span>
      </div>

      {/* Upper jaw */}
      <div className="grid grid-cols-2 gap-2">
        <QuadrantRow
          label="Q1 — Overkæbe højre"
          teeth={Q1_TEETH}
          cells={state.cells}
          missingTeeth={state.missingTeeth}
          isReadOnly={isReadOnly}
          dispatch={dispatch}
          highlightedSextantTeeth={highlightedSextantTeeth}
        />
        <QuadrantRow
          label="Q2 — Overkæbe venstre"
          teeth={Q2_TEETH}
          cells={state.cells}
          missingTeeth={state.missingTeeth}
          isReadOnly={isReadOnly}
          dispatch={dispatch}
          highlightedSextantTeeth={highlightedSextantTeeth}
        />
      </div>

      {/* Lower jaw */}
      <div className="grid grid-cols-2 gap-2">
        <QuadrantRow
          label="Q4 — Underkæbe højre"
          teeth={Q4_TEETH}
          cells={state.cells}
          missingTeeth={state.missingTeeth}
          isReadOnly={isReadOnly}
          dispatch={dispatch}
          highlightedSextantTeeth={highlightedSextantTeeth}
        />
        <QuadrantRow
          label="Q3 — Underkæbe venstre"
          teeth={Q3_TEETH}
          cells={state.cells}
          missingTeeth={state.missingTeeth}
          isReadOnly={isReadOnly}
          dispatch={dispatch}
          highlightedSextantTeeth={highlightedSextantTeeth}
        />
      </div>
    </div>
  );
}

